import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Variable } from '@apis/cas/generated';
import { DataReaderService } from '@core/lp/data-reader.service';
import { ISaveState, SAVE_SERVICE } from '@core/saves/saves.interface';
import { filter, firstValueFrom } from 'rxjs';

export type VariableValues = Record<string, { variable: Variable; value: undefined | string }>;

@Injectable({
  providedIn: 'root',
})
export class LpVariablesService {
  allVariables = signal<Variable[]>([]);
  public state = signal<VariableValues>({});
  public stateList = computed(() =>
    [...Object.entries(this.state())].map(([, { variable, value }]) => ({
      variable,
      value,
    })),
  );
  public namedDictionary = computed(() => {
    const record: Record<string, string> = {};
    for (const [, { variable, value }] of Object.entries(this.state())) {
      record[variable.name] = value ?? '';
    }
    return record;
  });
  readonly #dataReaderService = inject(DataReaderService);
  readonly #saveService = inject(SAVE_SERVICE);
  private isInitialized = signal(false);
  private isInitialized$ = toObservable(this.isInitialized);

  constructor() {
    this.#dataReaderService.getAllVariables().then((variables) => {
      this.allVariables.set(variables);
      this.initialize(variables);
    });
    // This is insurance that, if this service is already created, but the player logs out and then in again
    // We need to use the new player's variable values
    this.#saveService.onLogin$.subscribe(async () => {
      console.log('Just logged in so we are initializing the variables again.');
      await this.waitForInitialization();
      //call initialize again
      this.initialize(this.allVariables());
    });
  }

  public async waitForInitialization() {
    if (this.isInitialized()) return;

    await firstValueFrom(this.isInitialized$.pipe(filter((state) => state)));
  }

  public subscribe() {
    this.state();
  }

  public has(id: string) {
    return this.getVariable(id) !== undefined;
  }

  public set(variableOrId: Variable | string, value: string | undefined) {
    const variable = typeof variableOrId === 'string' ? this.getVariable(variableOrId) : variableOrId;
    if (!variable) {
      throw new Error(`Variable '${variableOrId}' not found`);
    }

    this.state.set({
      ...this.state(),
      [variable.variableRef]: { variable, value },
    });
    this.save();
  }

  public tryGet(id: string): string | undefined {
    const variable = this.getVariable(id);
    if (!variable) return undefined;
    return this.state()[variable.variableRef]?.value;
  }

  public get(id: string): string {
    if (!this.has(id)) {
      throw new Error(`Variable '${id}' not found`);
    }

    const value = this.tryGet(id);
    if (value === undefined) {
      throw new Error(`Variable '${id}' not set`);
    }

    return value;
  }

  async save() {
    const previousState = await this.#saveService.load();
    const newState: ISaveState = {
      ...previousState,
      variables: this.state(),
    };
    await this.#saveService.save(newState);
  }

  private async initialize(variables: Variable[]) {
    console.log('Initializing variables:', variables);
    this.isInitialized.set(false);
    const savedVariableValues = (await this.#saveService.load()).variables;
    const newState: VariableValues = {};
    for (const variable of variables) {
      const savedValue = savedVariableValues?.[variable.variableRef]?.value;
      newState[variable.variableRef] = {
        variable,
        value: savedValue !== undefined ? savedValue : variable.startValue === '' ? undefined : variable.startValue,
      };
    }
    this.state.set(newState);
    this.isInitialized.set(true);
  }

  private getVariable(id: string): Variable | undefined {
    return this.state()[id]?.variable;
  }
}
